import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import {
  getHasAdminSessionParam,
  setInterceptorUseAdminSession,
} from "src/domains/http/interceptors/use-admin-session-interceptor"
import { useQuery } from "src/domains/http/use-query"

import {
  clearStoredSelectedSession,
  findStoredSelectedSession,
  storeSelectedSession,
} from "./selected-session-storage"
import { fetchSessions } from "./session-queries"
import { TFetchSessionsResponse } from "./session-types"
import { useRedirectToAppSelect } from "./use-redirect-to-app-select"

type TSession = TFetchSessionsResponse["sessions"][number]

type TSessionContext = {
  sessions: TSession[]
  isLoading: boolean
  isError: boolean
  selectedSession: TSession | undefined
  setSelectedSession: (session: TSession) => void
}

const SessionContext = createContext<TSessionContext | undefined>(undefined)

/**
 * Loads the user's active sessions and exposes them (plus the resolved admin
 * mode) to the app via {@link useSession}.
 *
 * On the guarded auth routes (`/login`, `/signup`) it also handles an already
 * authenticated user by redirecting them to `/account-redirect`, where they click
 * the account to continue with (one card per session). It leaves them on the
 * form when the URL requests an admin session they don't hold yet (so they can
 * sign in as admin), or when the `skip_session_select` param is set (so they can
 * sign in with a different account from the selection screen without bouncing back).
 * Admin mode is derived from the selected session and kept in sync with the request
 * interceptor so outgoing requests carry `use_minut_admin_session` when appropriate.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useQuery(fetchSessions)
  const sessions = data?.sessions ?? []

  // The session the user is acting as. Undefined until the sessions load.
  const [selectedSession, setSelectedSessionState] = useState<
    TSession | undefined
  >(undefined)

  useRedirectToAppSelect(sessions)

  const hasAdminSession = sessions.some((session) => session.is_admin)

  // Once the sessions load, restore the last selection from localStorage when it
  // still matches a loaded session; otherwise clear the stale value and default
  // to the first session.
  useEffect(() => {
    if (!data) {
      return
    }
    const match = findStoredSelectedSession(data.sessions)
    if (!match) {
      clearStoredSelectedSession()
      setSelectedSessionState(data.sessions[0])
      return
    }
    setSelectedSessionState(match)
  }, [data])

  // Persist the selection so it survives reloads, then update state.
  const setSelectedSession = useCallback((session: TSession) => {
    storeSelectedSession(session)
    setSelectedSessionState(session)
  }, [])

  // Admin mode for outgoing requests: on when the selected session is an admin
  // session, or seeded on when the URL requests an admin session the user does
  // not hold yet (so a personal-only user keeps sending `admin` while signing in
  // as admin). `||` is deliberate: a selected personal session (`is_admin ===
  // false`) must still fall through to the seeded value.
  const shouldUseAdminSession =
    selectedSession?.is_admin === true ||
    (getHasAdminSessionParam() && !hasAdminSession)

  // Keep the non-React request interceptor's view in sync with the resolved
  // value, so requests made after the sessions load (or after the user switches
  // accounts) carry `admin`.
  useEffect(() => {
    setInterceptorUseAdminSession(shouldUseAdminSession)
  }, [shouldUseAdminSession])

  const value: TSessionContext = {
    sessions,
    isLoading,
    isError,
    selectedSession,
    setSelectedSession,
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)

  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider")
  }

  return context
}
